'use client';

import React, { useState, useCallback } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import { Button } from '@/core/presentation/components/ui/Button';
import { ScheduleMaintenanceModal } from '@/features/app/presentation/components/ScheduleMaintenanceModal';
import {
  LEMBRETES_ACTION_MARK_CONTACT,
  LEMBRETES_ACTION_MARK_SCHEDULED,
  LEMBRETES_BADGE_AGENDADO,
  LEMBRETES_BADGE_ATRASADO,
  LEMBRETES_BADGE_CONTATADO,
  LEMBRETES_LABEL_AGENDADA_PARA,
  LEMBRETES_LABEL_ENVIADO,
  LEMBRETES_LABEL_MANUT_PREVISTA,
  LEMBRETES_LABEL_PREVISTA,
  LEMBRETES_LABEL_VALUE_CHARGED,
  LEMBRETES_LIST_EMPTY_ENVIADOS,
  LEMBRETES_LIST_EMPTY_FILTERED,
  LEMBRETES_LIST_EMPTY_FUTUROS,
  LEMBRETES_LIST_EMPTY_FUTUROS_DAY,
  LEMBRETES_LIST_SECTION_ENVIADOS,
  LEMBRETES_LIST_SECTION_FUTUROS,
  LEMBRETES_TOAST_CONTACT_OK,
  LEMBRETES_TOAST_SCHEDULE_OK,
} from '@/features/app/presentation/constants/lembretes-calendario-copy';
import { normalizeSentAt } from '@/features/app/presentation/utils/reminder-day-metrics';
import type { ReminderOperationResult } from '@/features/app/reminders/presentation/context/ReminderContext';

function formatDate(iso: string): string {
  if (!iso) return '—';
  const dayKey = iso.trim().length >= 10 ? iso.trim().slice(0, 10) : iso.trim();
  return new Date(`${dayKey}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function isLate(expectedDate: string): boolean {
  return expectedDate < todayISO();
}

function hasContact(r: Reminder): boolean {
  return r.contacted_at != null && String(r.contacted_at).trim() !== '';
}

function hasSchedule(r: Reminder): boolean {
  return r.scheduled_date != null && String(r.scheduled_date).trim() !== '';
}

export interface LembretesListPanelProps {
  enviados: Reminder[];
  futuros: Reminder[];
  showEnviadosSection: boolean;
  showFuturosSection: boolean;
  showSectionTitles: boolean;
  getClientName: (clientId: string) => string;
  /** Mensagem quando a seção enviados está vazia (global ou filtro por dia). */
  emptyEnviadosMessage: string;
  /** Mensagem quando a seção futuros está vazia. */
  emptyFuturosMessage: string;
  onUpdateReminder: (id: string, partial: Partial<Reminder>) => Promise<ReminderOperationResult>;
  onActionSuccess: (message: string) => void;
  onActionError?: (message: string) => void;
}

export const LembretesListPanel: React.FC<LembretesListPanelProps> = ({
  enviados,
  futuros,
  showEnviadosSection,
  showFuturosSection,
  showSectionTitles,
  getClientName,
  emptyEnviadosMessage,
  emptyFuturosMessage,
  onUpdateReminder,
  onActionSuccess,
  onActionError,
}) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [scheduleReminder, setScheduleReminder] = useState<Reminder | null>(null);

  const runUpdate = async (id: string, partial: Partial<Reminder>, okMessage: string) => {
    setBusyId(id);
    try {
      const res = await onUpdateReminder(id, partial);
      if (res.success) {
        onActionSuccess(okMessage);
      } else if (res.error && onActionError) {
        onActionError(res.error);
      }
    } finally {
      setBusyId(null);
    }
  };

  const markContact = (r: Reminder) => {
    void runUpdate(
      r.id,
      { contacted_at: new Date().toISOString(), status: 'contacted' },
      LEMBRETES_TOAST_CONTACT_OK
    );
  };

  const handleScheduleModalSubmit = useCallback(
    async (dateKey: string, valueReais: number): Promise<boolean> => {
      if (!scheduleReminder) return false;
      setBusyId(scheduleReminder.id);
      try {
        const res = await onUpdateReminder(scheduleReminder.id, {
          scheduled_date: `${dateKey}T12:00:00.000Z`,
          value_charged: valueReais,
          status: 'scheduled',
        });
        if (res.success) {
          onActionSuccess(LEMBRETES_TOAST_SCHEDULE_OK);
          return true;
        }
        if (res.error && onActionError) {
          onActionError(res.error);
        }
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [scheduleReminder, onUpdateReminder, onActionSuccess, onActionError]
  );

  return (
    <div className="space-y-6 min-w-0">
      {showEnviadosSection && (
        <div className="space-y-3 min-w-0">
          {showSectionTitles ? (
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {LEMBRETES_LIST_SECTION_ENVIADOS}
            </h3>
          ) : null}
          {enviados.length === 0 ? (
            <div className="rounded-xl border border-border-light bg-[#fafbfc] p-6 text-center">
              <AppIcon name="EnvelopeIcon" size={36} className="mx-auto text-text-muted mb-2" />
              <p className="text-text-muted text-sm">{emptyEnviadosMessage}</p>
            </div>
          ) : (
            <ul className="space-y-3 min-w-0 list-none pl-0">
              {enviados.map((r) => {
                const sentDates = normalizeSentAt(r.sent_at);
                const lastSent = sentDates.length > 0 ? sentDates[sentDates.length - 1] : '';
                const lastSentDate = lastSent ? lastSent.slice(0, 10) : '';
                const atrasado = isLate(r.expected_date);
                const contacted = hasContact(r);
                const scheduled = hasSchedule(r);
                const busy = busyId === r.id;

                return (
                  <li
                    key={r.id}
                    className={`rounded-xl border bg-white overflow-hidden transition-colors min-w-0 ${
                      atrasado ? 'border-amber-300 bg-amber-50/50' : 'border-border-light'
                    }`}
                  >
                    <div className="p-4 sm:p-5 min-w-0 overflow-hidden">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <AppIcon name="UserCircleIcon" size={20} className="text-cyan shrink-0" />
                            <span className="font-semibold text-navy-dark">{getClientName(r.client_id)}</span>
                            {atrasado && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5">
                                <AppIcon name="ExclamationTriangleIcon" size={14} />
                                {LEMBRETES_BADGE_ATRASADO}
                              </span>
                            )}
                            {contacted && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-cyan/15 text-cyan-dark text-xs font-medium px-2 py-0.5">
                                {LEMBRETES_BADGE_CONTATADO}
                              </span>
                            )}
                            {scheduled && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-0.5">
                                {LEMBRETES_BADGE_AGENDADO}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-0.5 mt-1.5 text-sm text-text-muted min-w-0">
                            <span className="flex items-center gap-1 min-w-0">
                              <AppIcon name="PaperAirplaneIcon" size={16} className="shrink-0" />
                              <span className="overflow-hidden text-ellipsis">
                                {LEMBRETES_LABEL_ENVIADO} {formatDate(lastSentDate)}
                              </span>
                            </span>
                            <span className="flex items-center gap-1 min-w-0 overflow-hidden">
                              <AppIcon name="CalendarDaysIcon" size={16} className="shrink-0" />
                              <span className="overflow-hidden text-ellipsis">
                                {LEMBRETES_LABEL_PREVISTA} {formatDate(r.expected_date)}
                              </span>
                            </span>
                            {scheduled && r.scheduled_date && (
                              <span className="flex items-center gap-1 min-w-0 overflow-hidden text-emerald-800/90">
                                <AppIcon name="CheckCircleIcon" size={16} className="shrink-0" />
                                <span className="overflow-hidden text-ellipsis">
                                  {LEMBRETES_LABEL_AGENDADA_PARA} {formatDate(r.scheduled_date)}
                                </span>
                              </span>
                            )}
                            {r.value_charged != null && Number.isFinite(r.value_charged) && (
                              <span className="flex items-center gap-1 min-w-0 text-sm text-navy-dark font-medium">
                                <AppIcon name="BanknotesIcon" size={16} className="shrink-0 text-cyan" />
                                {LEMBRETES_LABEL_VALUE_CHARGED}{' '}
                                {r.value_charged.toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })}
                              </span>
                            )}
                          </div>
                          {r.equipment_reference && (
                            <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                              <AppIcon name="TagIcon" size={14} />
                              {r.equipment_reference}
                            </p>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {!contacted && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="!py-1.5 !px-3 text-xs"
                                disabled={busy}
                                onClick={() => markContact(r)}
                              >
                                {LEMBRETES_ACTION_MARK_CONTACT}
                              </Button>
                            )}
                            {!scheduled && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="!py-1.5 !px-3 text-xs"
                                disabled={busy}
                                onClick={() => setScheduleReminder(r)}
                              >
                                {LEMBRETES_ACTION_MARK_SCHEDULED}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {showFuturosSection && (
        <div className="space-y-3 min-w-0">
          {showSectionTitles ? (
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {LEMBRETES_LIST_SECTION_FUTUROS}
            </h3>
          ) : null}
          {futuros.length === 0 ? (
            <div className="rounded-xl border border-border-light bg-[#fafbfc] p-6 text-center">
              <AppIcon name="BellAlertIcon" size={36} className="mx-auto text-text-muted mb-2" />
              <p className="text-text-muted text-sm">{emptyFuturosMessage}</p>
            </div>
          ) : (
            <ul className="space-y-3 min-w-0 list-none pl-0">
              {futuros.map((r) => (
                <li key={r.id} className="rounded-xl border border-border-light bg-white p-4 sm:p-5 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <AppIcon name="UserCircleIcon" size={20} className="text-cyan shrink-0" />
                        <span className="font-semibold text-navy-dark">{getClientName(r.client_id)}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-0.5 mt-1.5 text-sm text-text-muted min-w-0">
                        <span className="flex items-center gap-1 min-w-0">
                          <AppIcon name="CalendarDaysIcon" size={16} className="shrink-0" />
                          <span className="overflow-hidden text-ellipsis">
                            {LEMBRETES_LABEL_MANUT_PREVISTA} {formatDate(r.expected_date)}
                          </span>
                        </span>
                      </div>
                      {r.equipment_reference && (
                        <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                          <AppIcon name="TagIcon" size={14} />
                          {r.equipment_reference}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ScheduleMaintenanceModal
        isOpen={scheduleReminder != null}
        onClose={() => setScheduleReminder(null)}
        reminder={scheduleReminder}
        submitting={scheduleReminder != null && busyId === scheduleReminder.id}
        onSubmit={handleScheduleModalSubmit}
        onValidationError={(msg) => onActionError?.(msg)}
      />
    </div>
  );
};
